<template>
  <div
    class="carousel"
    :class="{
      'carousel--navigation-visible': navigationVisible,
      'carousel__no-slider': slidesCount === 1,
      ['carousel--item_' + activeSlide]: true,
      ['carousel--count_' + slidesCount]: true,
    }"
  >
    <a
      v-if="showSkipSliderLink"
      :href="`#${skipToBottomLink}`"
      class="carousel__skip-to-bottom-link button button--secondary-small"
      tabindex="0"
    >
      {{ $t('slider.skip_to_bottom') }}
    </a>
    <div
      class="carousel__wrapper"
      ref="slider"
    >
      <div
        class="carousel__container"
        :class="{
          'carousel__container--slide-single': slidesCount === 1
        }"
      >
        <div
          class="carousel__slide"
          v-for="(slide, index) in slidesCount"
          :key="`slide-${index}`"
          :class="{
            'carousel__slide--single': slidesCount === 1,
            'carousel__slide--previous-active': options?.effect && options.effect === 'fade' && previousActive !== activeSlide && previousActive === index,
          }"
        >
          <slot :name="`slide-${index}`" />
        </div>
      </div>
    </div>
    <div
      class="carousel__thumbnail-wrapper"
      v-if="thumbnails && thumbnails.length > 0 && thumbnails.length < 5"
    >
      <div class="carousel__thumbnail-wrapper-inner">
        <span
          class="carousel__thumbnail-button"
          :class="{
            'carousel__thumbnail-button--active': index === activeSlide
          }"
          v-for="(thumb, index) in thumbnails"
          :key="`${index}-thumb`"
          @click.prevent="changeSlide(index)"
        >
          <img
            :src="thumb.src"
            :alt="thumb.name"
            loading="lazy"
          >
        </span>
      </div>
    </div>
    <div
      class="carousel__pagination-wrapper--one-slide"
      v-if="slidesCount === 1"
    />
    <div
      class="carousel__pagination-wrapper"
      v-if="slidesCount > 1"
    >
      <span
        class="carousel__button carousel__button--prev"
        ref="buttonPrev"
      >
        <span class="carousel__button-icon" />
      </span>
      <div
        v-if="showPagination"
        class="carousel__pagination"
        :class="{
          'carousel__pagination--show': showPagination,
          'carousel__pagination--visible': slidesCount > 1
        }"
      >
        <span
          class="carousel__pagination-button"
          :class="{
            'carousel__pagination-button--active': index === activeSlide
          }"
          v-for="(slide, index) in slidesCount"
          :key="`${index}-button`"
          @click.prevent="changeSlide(index)"
        >
          <span class="carousel__pagination-progress-bar" />
        </span>
      </div>
      <div
        class="carousel__page-info"
        v-html="pageInfo"
      />
      <span
        class="carousel__button carousel__button--next"
        ref="buttonNext"
      >
        <span class="carousel__button-icon" />
      </span>
    </div>
    <div
      v-if="showSkipSliderLink"
      class="carousel__skip-to-bottom-anchor"
      :id="skipToBottomLink"
    />
  </div>
</template>

<script>
  import Swiper, {Autoplay, Navigation, Pagination, EffectFade, EffectFlip} from 'swiper'
  import {breakpoints} from '../../variables/variables.js';
  import pigeon from '../../utilities/js/pigeon/pigeon.js';
  import LazyLoad from '../lazyLoad/lazyLoad';
  import ImageElement from '../imageElement/imageElement';
  import {intersectionService} from '../../utilities/js/intersectionService/intersectionService';
  import {documentHelper} from '../../utilities/js/documentHelper/documentHelper';
  import {usePlaceholder} from '../../utilities/vueMixins/placeholderMixin/placeholder';

  const Carousel = {
    name: 'carousel',
    components: {
      ImageElement,
      LazyLoad
    },
    props: {
      placeholder: String,
      slidesCount: Number,
      slidesPerView: {
        type: [String, Number]
      },
      slidesPerViewDesktop: {
        type: [String, Number],
        default: 4
      },
      slidesPerViewLaptop: Number,
      slidesPerViewTabletLandscape: Number,
      slidesPerViewTablet: Number,
      slidesPerViewMobile: Number,
      thumbnails: {
        type: [Array]
      },
      loop: {
        type: Boolean,
        default: false
      },
      showPagination: {
        type: Boolean,
        default: false
      },
      lazyLoading: {
        type: Boolean,
        default: false
      },
      preloadSlideCount: {
        type: Number,
        default: 0
      },
      options: {
        type: Object,
        default: () => {}
      },
      showSkipSliderLink: {
        type: Boolean,
        default: false
      },
    },
    data() {
      return {
        activeSlide: 0,
        previousActive: 0,
        slidesPerGroup: 0,
        navigationVisible: false,
        skipToBottomLink: `skip-id--${this.$.uid}`
      };
    },
    mounted() {
      this.createSwiperInstance();
      if (this.loop) {
        // When creating clones for the loop, swiper has to wait for translations to load
        pigeon.subscribe('app:translationsComplete', () => {
          this.$nextTick(() => {
            this.destroySwiperInstance();
            this.createSwiperInstance();
          });
        });
      }
      pigeon.publish('carousel:mounted');
    },
    watch: {
      slidesCount() {
        this.$nextTick(() => {
          this.destroySwiperInstance();
          this.createSwiperInstance();
        })
      }
    },
    methods: {
      createSwiperInstance() {
        const defaultOptions = {
          slidesPerView: 1,
          loop: this.loop,
          speed: 600,
          wrapperClass: 'carousel__container',
          slideClass: 'carousel__slide',
          slideActiveClass: 'carousel__slide--active',
          slidesOffsetBefore: 0,
          slidesOffsetAfter: 0,
          spaceBetween: 0,
          modules: [],
          allowTouchMove: this.slidesCount > 1,
          navigation: {
            nextEl: this.$refs.buttonNext,
            prevEl: this.$refs.buttonPrev,
          },
          breakpoints: {
            [breakpoints.phoneLg]: {
              slidesPerView: this.slidesPerView ||
                this.slidesPerViewTablet ||
                this.slidesPerViewTabletLandscape ||
                this.slidesPerViewLaptop ||
                this.slidesPerViewDesktop
            },
            [breakpoints.tablet]: {
              slidesPerView: this.slidesPerView ||
                this.slidesPerViewTabletLandscape ||
                this.slidesPerViewLaptop ||
                this.slidesPerViewDesktop
            },
            [breakpoints.tabletLandscape]: {
              slidesPerView: this.slidesPerView ||
                this.slidesPerViewLaptop ||
                this.slidesPerViewDesktop
            },
            [breakpoints.laptop]: {
              slidesPerView: this.slidesPerView ||
                this.slidesPerViewDesktop
            },
          },
          on: {
            init: () => {
              this.setNavigationClass();

              this.$nextTick(() => {
                if (this.activeSlide !== 0) {
                  this.changeSlide(this.activeSlide);
                }
              });
            },
            resize: () => {
              this.setNavigationClass();
              this.slideChangeHandler();
            }
          }
        };

        if (this.slidesCount > 1) {
          const finalOptions = this.processOptions({...defaultOptions, ...this.options});
          this.swiperInstance = new Swiper(this.$refs.slider, finalOptions);
          this.slidesPerGroup = this.swiperInstance.params.slidesPerGroup;

          this.swiperInstance.on('slideChange', this.slideChangeHandler.bind(this));
          this.swiperInstance.on('slideChangeTransitionStart', this.lazyLoadHandler.bind(this));
          this.swiperInstance.on('slideChangeTransitionEnd', this.slideChangeEndHandler.bind(this));
        } else if (this.slidesCount === 1) {
          this.$refs.slider.querySelector('.carousel__slide').classList.add('carousel__slide--active');
        }

        this.$nextTick(() => {
          this.lazyLoadActiveHandler();
          this.$emit('created');

          if (this.slidesCount > 1 && this.swiperInstance) {
            this.slideChangeHandler();
          }
          usePlaceholder(this.placeholder);
        });

        documentHelper.ready(() => {
          setTimeout(() => { this.lazyLoadHandler(); }, 5000);
        });
      },
      destroySwiperInstance(deleteInstance = true, cleanStyles = true) {
        if (this.swiperInstance) {
          this.swiperInstance.destroy(deleteInstance, cleanStyles);
        }
      },
      setNavigationClass() {
        const container = this.$refs.slider;
        if (container) {
          const slides = container.querySelectorAll('.carousel__slide');
          if (slides) {
            let innerWidth = 0;
            for (let i = 0, length = slides.length; i < length; i += 1) {
              innerWidth += slides[i].offsetWidth;
            }
            this.navigationVisible = container.offsetWidth < innerWidth;
          }
        }
      },
      lazyLoadActiveHandler() {
        if (this.lazyLoading) {
          const container = this.$refs.slider;
          if (container) {
            const activeSlide = container.querySelector('.carousel__slide--active');
            if (activeSlide) {
              this.swiperLazyLoadSlide(activeSlide);
              this.vueLazyLoadSlide(activeSlide);
            }
          }
        }
      },
      lazyLoadHandler() {
        if (this.lazyLoading || this.preloadSlideCount > 0) {
          const container = this.$refs.slider;
          if (container) {
            const activeSlide = container.querySelector('.carousel__slide--active');
            if (activeSlide) {
              this.swiperLazyLoadSlide(activeSlide);
              if (this.preloadSlideCount > 0 && this.swiperInstance) {
                const l = this.preloadSlideCount + this.swiperInstance.params.slidesPerGroup - 1;
                let nextSlide = activeSlide;
                let prevSlide = activeSlide;
                for (let i = 1; i <= l; i += 1) {
                  nextSlide = nextSlide?.nextElementSibling;
                  this.vueLazyLoadSlide(nextSlide);
                  this.swiperLazyLoadSlide(nextSlide);
                }
                for (let i = 1; i <= l; i += 1) {
                  prevSlide = prevSlide?.previousElementSibling;
                  this.vueLazyLoadSlide(prevSlide);
                  this.swiperLazyLoadSlide(prevSlide);
                }
              }
            }
          }
        }
      },
      vueLazyLoadSlide(slide) {
        // preload all lazy-load elements in next slide
        if (slide) {
          intersectionService.activateElement('lazyload', slide);
        }
      },
      swiperLazyLoadSlide(slide) {
        // Lazy loader with <picture> <source data-srcset> <img data-src>
        if (slide) {
          // lazy load sprites and illustrations
          const className = slide.querySelectorAll('div[data-class-name]');
          if (className) {
            // remove loader
            const loader = slide.querySelector('.swiper-lazy-preloader');
            if (loader) {
              loader.parentNode.removeChild(loader);
            }
            // set img src
            for (let i = 0, l = className.length; i < l; i += 1) {
              className[i].setAttribute('class', className[i].getAttribute('data-class-name'));
              className[i].removeAttribute('data-class-name');
            }
          }

          // lazy load images
          const img = slide.querySelectorAll('img[data-src]');
          if (img) {
            // remove loader
            const loader = slide.querySelector('.swiper-lazy-preloader');
            if (loader) {
              loader.parentNode.removeChild(loader);
            }
            // set img src
            for (let i = 0, l = img.length; i < l; i += 1) {
              img[i].setAttribute('src', img[i].getAttribute('data-src'));
              img[i].removeAttribute('data-src');
            }
          }

          // lazy load sources
          const sources = slide.querySelectorAll('source[data-srcset]');
          if (sources) {
            // set source srcset
            for (let i = 0, l = sources.length; i < l; i += 1) {
              sources[i].setAttribute('srcset', sources[i].getAttribute('data-srcset'));
              sources[i].removeAttribute('data-srcset');
            }
          }
        }
      },
      slideChangeHandler() {
        if (this.activeSlide !== this.swiperInstance.realIndex || this.slidesPerGroup !== this.swiperInstance.params.slidesPerGroup) {
          this.activeSlide = this.swiperInstance.realIndex;
          this.slidesPerGroup = this.swiperInstance.params.slidesPerGroup;
          this.$emit('change', this.activeSlide);
        }
      },
      slideChangeEndHandler() {
        if (this.options?.effect === 'fade') {
          this.previousActive = this.activeSlide;
        }
      },
      changeSlide(slideIndex) {
        // swiper slideTo function slides wrong, when loop is activated
        const slideToIndex = (this.loop) ? slideIndex + 1 : slideIndex;
        if (this.swiperInstance) {
          this.swiperInstance.slideTo(slideToIndex);
        }
      },
      processOptions(options){
        if (options.autoplay){
          options.modules.push(Autoplay);
        }

        if (options.navigation){
          options.modules.push(Navigation);
        }

        if (options.Pagination || this.showPagination){
          options.modules.push(Pagination);
        }

        options = this.processEffect(options);

        return options;
      },
      processEffect(options){
        if (!options.effect) return options;

        if (this.options.effect === 'fade' &&
          navigator.userAgent.indexOf('Firefox') !== -1) {
          this.options.effect = 'slide';
        }

        if (this.options.effect === 'fade') {
          options.modules.push(EffectFade);
        }

        if (this.options.effect === 'flip') {
          options.modules.push(EffectFlip);
        }

        return options;
      },
    },
    computed: {
      pageInfo() {
        const maxPage = Math.ceil(this.slidesCount / this.slidesPerGroup);
        const curPage = Math.round(this.activeSlide / this.slidesPerGroup) + 1;
        return `<span>${curPage}</span> <span>${this.$t('slider.page_info')}</span> <span>${maxPage}</span>`;
      }
    }
  };

  export default Carousel;
</script>
